/**
 * What the models read (SYR-111 phase 3).
 *
 * Customers see their sources (Steve 2026-09-22): a record problem is only
 * actionable if you know which pages produced it. Composition first, because
 * "mostly review sites, little of your own" is the finding — the domain list
 * is the evidence for it.
 */

import React from 'react';
import { Space, Table, Tag, Typography } from 'antd';
import { Card } from '../../atoms';
import { ReadSection } from './ReadSection';
import { THEME } from '../../config/theme';

const { Text } = Typography;

const KIND_LABEL: Record<string, string> = {
  brand: 'your own pages',
  review: 'review sites',
  news: 'news and press',
  marketplace: 'marketplaces',
  retailer: 'retailers',
  competitor: 'competitors',
  social: 'social',
  other: 'everything else',
};

interface LedgerEntry {
  domain: string;
  kind: string;
  count: number;
}

interface Props {
  sources: {
    ledger?: {
      byKind?: { kind: string; count: number; share: number }[];
      entries?: LedgerEntry[];
      totalCitations?: number;
      distinctDomains?: number;
    } | null;
  } | null;
  // The section's number; one later when the read has a pages section.
  n?: string;
}

export const SourcesPanel: React.FunctionComponent<Props> = ({ sources, n = '6' }) => {
  const ledger = sources?.ledger;
  if (!ledger?.entries?.length) return null;

  const byKind = [...(ledger.byKind ?? [])].sort((a, b) => b.count - a.count);
  const own = byKind.find((k) => k.kind === 'brand');

  return (
    <ReadSection
      id="sources"
      n={n}
      title="What did the models read?"
      sub="When the models searched the web to answer about you, these are the pages they opened, and how many times. This is where their searched beliefs came from, so a fix aimed at a page nobody read changes nothing."
    >
      <Card>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space size={8} wrap>
          {byKind.map((kind) => (
            <Tag key={kind.kind} color={kind.kind === 'brand' ? 'blue' : 'default'}>
              {KIND_LABEL[kind.kind] ?? kind.kind} {Math.round(kind.share * 100)}%
            </Tag>
          ))}
        </Space>

        <Text style={{ color: THEME.textSecondary, fontSize: 13 }}>
          {ledger.totalCitations?.toLocaleString()} citations across{' '}
          {ledger.distinctDomains} domains
          {own
            ? `, of which ${Math.round(own.share * 100)}% were your own pages.`
            : ', none of them your own pages.'}
        </Text>

        <Table
          size="small"
          pagination={false}
          rowKey="domain"
          dataSource={(ledger.entries ?? []).slice(0, 12)}
          columns={[
            { title: 'Domain', dataIndex: 'domain' },
            {
              title: 'Kind',
              dataIndex: 'kind',
              width: 150,
              render: (kind: string) => (
                <Text style={{ color: THEME.textSecondary }}>
                  {KIND_LABEL[kind] ?? kind}
                </Text>
              ),
            },
            { title: 'Times read', dataIndex: 'count', width: 110 },
          ]}
        />

      </Space>
      </Card>
    </ReadSection>
  );
};
